/* Login — "mission control" entry point.
   Left: warm neumorphic auth panel (email / Google / SSO).
   Right: a live, looping pipeline console — the product working while you sign in. */

const { useState: useStateL, useEffect: useEffectL, useRef: useRefL } = React;

function GoogleG({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.1 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.3 17.6 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7C43.7 38 46.5 31.9 46.5 24.5z"/>
      <path fill="#FBBC05" d="M10.4 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.8-6.1C.9 16.4 0 20.1 0 24s.9 7.6 2.6 10.8l7.8-6.1z"/>
      <path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.5l-7.3-5.7c-2 1.4-4.7 2.3-7.7 2.3-6.4 0-11.7-3.8-13.6-9.3l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/>
    </svg>
  );
}

function AmbientConsole() {
  const stages = window.MAV.stageDefs;
  const log = window.MAV.liveLog;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [active, setActive] = useStateL(reduce ? 6 : 1);
  const [passFlash, setPassFlash] = useStateL(reduce);
  const [logIdx, setLogIdx] = useStateL(reduce ? log.length - 1 : 4);
  const [tokens, setTokens] = useStateL(reduce ? 412 : 76);
  const logRef = useRefL(null);

  // pipeline progression
  useEffectL(() => {
    if (reduce) return;
    const t = setInterval(() => {
      setActive(a => {
        if (a >= stages.length) {              // finished — flash PASS, then restart
          setPassFlash(true);
          setTimeout(() => { setPassFlash(false); setTokens(76); }, 1400);
          return 1;
        }
        return a + 1;
      });
    }, 1250);
    return () => clearInterval(t);
  }, []);

  // streaming log + ticking token meter
  useEffectL(() => {
    if (reduce) return;
    const t = setInterval(() => {
      setLogIdx(i => (i >= log.length - 1 ? 4 : i + 1));
      setTokens(v => Math.min(v + Math.round(10 + Math.random() * 26), 412));
    }, 1050);
    return () => clearInterval(t);
  }, []);

  useEffectL(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [logIdx]);

  const lines = log.slice(0, logIdx + 1);

  return (
    <div className="console">
      <div className="console-head">
        <ClientBadge slug="synchrony" size={28} />
        <span className="cjob">15012935_SYF_FLR_0176</span>
        <span className="csrc">PDF</span>
        {passFlash
          ? <span className="cpill pass"><span className="dot"></span>PASS</span>
          : <span className="cpill run"><span className="dot"></span>Running</span>}
      </div>

      <div className="cpipe">
        {stages.map((s, i) => {
          const status = passFlash ? 'done' : i < active ? 'done' : i === active ? 'running' : 'queued';
          const fill = status === 'done' ? '100%' : status === 'running' ? '55%' : '0';
          let glyph;
          if (status === 'done') glyph = <I.check size={18} />;
          else if (status === 'running') glyph = <div className="cspin"></div>;
          else glyph = <span className="mono" style={{ fontSize: 11, fontWeight: 700 }}>{s.no}</span>;
          return (
            <div key={s.id} className={'cnode ' + status}>
              <span className="cconn"><span style={{ width: fill }}></span></span>
              <div className="corb">{glyph}</div>
              <div className="clabel">{s.name}</div>
            </div>
          );
        })}
      </div>

      <div className="logwrap" ref={logRef}>
        {lines.map((l, i) => (
          <div key={i} className="log-line"><span className="ts">{l.t} </span><span className={l.c}>{l.m}</span></div>
        ))}
        <div className="log-line"><span className="cur">█</span></div>
      </div>

      <div className="console-foot">
        <div className="cmeter-wrap">
          <div className="cmeter-top"><span>Token spend · this build</span><span className="mono">{tokens}K</span></div>
          <div className="cmeter"><span style={{ width: (tokens / 460 * 100) + '%' }}></span></div>
        </div>
        <div className="cstat">
          <div className="cv">91%</div>
          <div className="ck">pass rate</div>
        </div>
        <div className="cstat">
          <div className="cv">2.7m</div>
          <div className="ck">avg build</div>
        </div>
      </div>
    </div>
  );
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function ForgotPassword({ onClose }) {
  const [email, setEmail] = useStateL('');
  const [sent, setSent] = useStateL(false);
  const [busy, setBusy] = useStateL(false);
  const ok = EMAIL_RE.test(email.trim());

  if (sent) {
    return ReactDOM.createPortal(
      <div className="fb-overlay" onClick={onClose}>
        <div className="fb-modal fb-done" onClick={e => e.stopPropagation()}>
          <div className="fb-done-ico"><I.send size={26} /></div>
          <h2 className="fb-done-h">Request sent</h2>
          <p className="fb-done-p">If <b>{email.trim()}</b> matches an account, your workspace admin has been notified and will issue you a one-time password. Ask your team lead if you don't hear back.</p>
          <div className="fb-done-acts">
            <button className="btn btn-primary" onClick={onClose}>Back to sign in</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="fb-head">
          <div>
            <div className="row gap-8" style={{ marginBottom: 7 }}><span className="chip" style={{ background: 'var(--brand-yellow)', color: 'var(--ink-on-brand)', fontWeight: 700 }}>Reset</span></div>
            <h2 className="fb-title">Forgot your password?</h2>
            <p className="fb-sub">Your workspace admin will issue you a one-time password to sign in with, then change it under Account → Security.</p>
          </div>
          <button className="icon-act" onClick={onClose} title="Close"><I.x size={16} /></button>
        </div>
        <div className="fb-body">
          <div className="ep-field" style={{ marginBottom: 0 }}>
            <label className="field-lbl">Work email</label>
            <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="aum@mavlers.com" autoFocus />
            {email.trim() && !ok && <span className="ep-err">Enter a valid email address.</span>}
          </div>
        </div>
        <div className="fb-foot">
          <span className="muted" style={{ fontSize: 11.5 }}>SSO users sign in via their provider</span>
          <div className="row gap-10">
            <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-lg" disabled={!ok || busy}
              onClick={async () => {
                setBusy(true);
                // the server answers identically whether or not the email exists — never an oracle
                try { await window.GooseAPI.forgotPassword(email.trim()); } catch (_) { /* generic either way */ }
                setBusy(false); setSent(true);
              }}><I.send size={15} /> Request a reset</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const REQUEST_ROLES = ['Product Manager', 'Admin / Workspace manager', 'Dev / Builder', 'SME / Reviewer', 'Team lead', 'Other'];

function RequestAccess({ onClose }) {
  const [name, setName] = useStateL('');
  const [email, setEmail] = useStateL('');
  const [company, setCompany] = useStateL('');
  const [role, setRole] = useStateL('Producer');
  const [msg, setMsg] = useStateL('');
  const [sent, setSent] = useStateL(false);
  const emailOk = EMAIL_RE.test(email.trim());
  const canSend = name.trim() && emailOk && company.trim();

  const send = () => {
    (window.MAV.accessRequests = window.MAV.accessRequests || []).push({ name: name.trim(), email: email.trim(), company: company.trim(), role, message: msg.trim(), when: 'just now', status: 'pending' });
    setSent(true);
  };

  if (sent) {
    return ReactDOM.createPortal(
      <div className="fb-overlay" onClick={onClose}>
        <div className="fb-modal fb-done" onClick={e => e.stopPropagation()}>
          <div className="fb-done-ico"><I.check size={28} /></div>
          <h2 className="fb-done-h">Request received</h2>
          <p className="fb-done-p">Thanks, <b>{name.trim().split(/\s+/)[0]}</b>. A workspace admin will review your request and email <b>{email.trim()}</b> if you're cleared for takeoff.</p>
          <div className="fb-done-acts">
            <button className="btn btn-primary" onClick={onClose}>Back to sign in</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="fb-overlay" onClick={onClose}>
      <div className="fb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="fb-head">
          <div>
            <div className="row gap-8" style={{ marginBottom: 7 }}><span className="chip" style={{ background: 'var(--brand-yellow)', color: 'var(--ink-on-brand)', fontWeight: 700 }}>Request access</span></div>
            <h2 className="fb-title">Join the squadron</h2>
            <p className="fb-sub">Tell us who you are — an admin reviews every request.</p>
          </div>
          <button className="icon-act" onClick={onClose} title="Close"><I.x size={16} /></button>
        </div>
        <div className="fb-body">
          <div className="ep-field"><label className="field-lbl">Full name</label><input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" autoFocus /></div>
          <div className="invite-grid">
            <div className="ep-field" style={{ margin: 0 }}>
              <label className="field-lbl">Work email</label>
              <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />
              {email.trim() && !emailOk && <span className="ep-err">Enter a valid email.</span>}
            </div>
            <div className="ep-field" style={{ margin: 0 }}>
              <label className="field-lbl">Company / workspace</label>
              <input className="input" value={company} onChange={e => setCompany(e.target.value)} placeholder="Mavlers" />
            </div>
          </div>
          <div className="ep-field" style={{ marginTop: 14 }}>
            <label className="field-lbl">Your role</label>
            <select className="input" value={role} onChange={e => setRole(e.target.value)}>
              {REQUEST_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="ep-field" style={{ marginTop: 14, marginBottom: 0 }}>
            <label className="field-lbl">Anything to add? <span className="muted" style={{ fontWeight: 500 }}>· optional</span></label>
            <textarea className="input" rows={2} value={msg} onChange={e => setMsg(e.target.value)} placeholder="How you'd use Goose, who referred you…" style={{ resize: 'none' }} />
          </div>
        </div>
        <div className="fb-foot">
          <span className="muted" style={{ fontSize: 11.5 }}>Reviewed by a workspace admin</span>
          <div className="row gap-10">
            <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-lg" onClick={send} disabled={!canSend} title={canSend ? 'Send request' : 'Name, valid email and company are required'}><I.arrow size={15} /> Request access</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Login({ onAuth }) {
  const [method, setMethod] = useStateL('email');
  const [show, setShow] = useStateL(false);
  const [remember, setRemember] = useStateL(true);
  const [busy, setBusy] = useStateL(false);
  const [authErr, setAuthErr] = useStateL('');
  const [modal, setModal] = useStateL(null);   // 'forgot' | 'request' | null
  const [theme, setTheme] = useStateL(() => (window.GooseTheme ? window.GooseTheme.get() : 'light'));
  useEffectL(() => {
    const h = (e) => setTheme(e.detail);
    window.addEventListener('goose-theme-change', h);
    return () => window.removeEventListener('goose-theme-change', h);
  }, []);
  const cycleTheme = () => { const T = window.GooseTheme.THEMES; window.GooseTheme.set(T[(T.indexOf(theme) + 1) % T.length]); };
  const themeIco = theme === 'light' ? <I.sun size={16} /> : theme === 'dark' ? <I.moon size={16} /> : <I.terminal size={16} />;

  // Real sign-in: POST /auth/login → session cookie → hydrate live data → enter.
  // Email method authenticates for real; Google/SSO have no backend yet, so they
  // enter on whatever session exists (else mock data). If the server is down we
  // still let the demo in with mock data; only a true 401 blocks with an error.
  const signIn = async (e) => {
    if (e) e.preventDefault();
    setAuthErr('');
    // SECURITY: only the email method has a real backend login. Google/SSO OAuth are NOT
    // wired yet — never enter the app without a successful real login (was a no-auth bypass:
    // clicking "Continue with Google"/SSO skipped the login call and went straight in).
    if (method !== 'email') {
      setAuthErr(method === 'google'
        ? 'Google sign-in isn’t available yet — sign in with email.'
        : 'SSO isn’t available yet — sign in with email.');
      return;
    }
    setBusy(true);
    // SECURITY: NO default credentials. Empty fields → empty login → backend 400/401 → stays
    // on the login screen with an error. (Was hardcoded aum@mavlers.com/devpass123 — clicking
    // "Sign in" with empty fields logged you straight in as a real product_manager admin.)
    let email = '', password = '';
    if (e && e.target && e.target.elements) {
      const el = e.target.elements;
      if (el.email && el.email.value) email = el.email.value.trim();
      if (el.password && el.password.value) password = el.password.value;
    }
    try {
      const loaded = await window.GooseAuthenticate(email, password);
      if (!loaded.ok) throw new Error(loaded.reason === 'auth' ? 'Session expired. Please sign in again.' : 'Signed in, but workspace data could not load. Please retry.');
      onAuth();
    } catch (err) {
      if (err && err.kind === 'auth') { setBusy(false); setAuthErr('Invalid email or password.'); }
      else if (err && err.kind === 'timeout') { setBusy(false); setAuthErr('Sign-in timed out — try again.'); }  // ISS-17: never an infinite spinner
      // SECURITY: a server-down 'network' error must NOT silently demo-mode into the app on a
      // real deploy (was a no-auth bypass when the API is unreachable). Surface it instead.
      else if (err && err.kind === 'network') { setBusy(false); setAuthErr('Can’t reach the server — please try again.'); }
      else { setBusy(false); setAuthErr((err && err.message) || 'Sign-in failed.'); }
    }
  };

  const methods = [
    { id: 'email', label: 'Email' },
    { id: 'google', label: 'Google' },
    { id: 'sso', label: 'SSO' },
  ];

  return (
    <div className="login">
      <button className="icon-btn theme-toggle login-theme-toggle" onClick={cycleTheme} title={'Theme: ' + window.GooseTheme.labels[theme] + ' · tap to switch'} aria-label="Cycle theme">{themeIco}</button>
      <div className="login-aside">
        <div className="brand">
          <div className="brand-mark goose" title="Every Maverick needs a Goose."><GooseMark size={30} /></div>
          <div>
            <div className="brand-name">Goose</div>
            <div className="brand-sub">Every Maverick needs a Goose.</div>
          </div>
        </div>

        <div className="login-core">
          <span className="login-eyebrow"><span className="live-dot"></span>All systems operational</span>
          <h1 className="login-h1">Client-ready email,<br /><span className="g">shipped on autopilot.</span></h1>
          <p className="login-sub">Sign in to the control plane. Submit a design, watch the six-stage squadron build it, review what needs your eyes, and deliver.</p>

          <div className="auth-seg" role="tablist">
            {methods.map(m => (
              <button key={m.id} role="tab" aria-selected={method === m.id}
                className={method === m.id ? 'sel' : ''} onClick={() => setMethod(m.id)}>
                {m.id === 'google' && <GoogleG size={15} />}
                {m.label}
              </button>
            ))}
          </div>

          {method === 'email' && (
            <form className="login-form" onSubmit={signIn}>
              <div className="login-field">
                <label className="field-lbl mb-0">Work email</label>
                <input className="input" type="email" name="email" placeholder="you@company.com" autoComplete="username" />
              </div>
              <div className="login-field">
                <label className="field-lbl mb-0">Password</label>
                <div className="input-affix">
                  <input className="input" type={show ? 'text' : 'password'} name="password" placeholder="••••••••••••" autoComplete="current-password" />
                  <button type="button" className="eye" onClick={() => setShow(s => !s)} aria-label="Toggle password">
                    {show
                      ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/><path d="M4 4l16 16"/></svg>
                      : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>}
                  </button>
                </div>
              </div>
              <div className="form-row">
                <label className="remember">
                  <span className={'check-box' + (remember ? ' checked' : '')} onClick={(e) => { e.preventDefault(); setRemember(r => !r); }}>{remember && <I.check size={12} />}</span>
                  Remember me
                </label>
                <a href="#" className="link-warm" onClick={(e) => { e.preventDefault(); setModal('forgot'); }}>Forgot password?</a>
              </div>
              {authErr && <div className="ep-err" style={{ marginBottom: 6 }}>{authErr}</div>}
              <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
                {busy ? <React.Fragment><span className="spinner" style={{ borderTopColor: 'var(--ink)' }}></span> Signing in…</React.Fragment>
                      : <React.Fragment>Sign in <I.arrow size={16} /></React.Fragment>}
              </button>
            </form>
          )}

          {method === 'google' && (
            <div className="login-form">
              <button className="btn btn-oauth" onClick={signIn} disabled={busy}>
                {busy ? <React.Fragment><span className="spinner" style={{ borderTopColor: 'var(--ink)' }}></span> Connecting…</React.Fragment>
                      : <React.Fragment><GoogleG size={18} /> Continue with Google</React.Fragment>}
              </button>
              <p className="oauth-note">Use your Mavlers Google Workspace account.<br />New domains are routed to admin approval.</p>
            </div>
          )}

          {method === 'sso' && (
            <form className="login-form" onSubmit={signIn}>
              <div className="login-field">
                <label className="field-lbl mb-0">Workspace domain</label>
                <input className="input" placeholder="your-team.company.com" autoComplete="organization" />
              </div>
              <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={busy}>
                {busy ? <React.Fragment><span className="spinner" style={{ borderTopColor: 'var(--ink)' }}></span> Redirecting…</React.Fragment>
                      : <React.Fragment>Continue with SSO <I.arrow size={16} /></React.Fragment>}
              </button>
              <p className="oauth-note">SAML / Okta single sign-on for teams.<br />You'll be redirected to your identity provider.</p>
            </form>
          )}
        </div>

        <div className="login-foot">
          <span>New to Goose? <a href="#" className="link-warm" onClick={(e) => { e.preventDefault(); setModal('request'); }}>Request access</a></span>
          <span className="mono" style={{ fontSize: 11, color: 'var(--faint)' }}>v1 · Mavlers</span>
        </div>
      </div>

      <div className="login-stage">
        <div className="float-chip top">
          <span className="fc-ico" style={{ background: 'var(--pass-bg)', color: 'var(--pass)' }}><I.check size={18} /></span>
          <div><div className="fc-v">128</div><div className="fc-k">builds this month</div></div>
        </div>
        <div className="float-chip bot">
          <span className="fc-ico" style={{ background: 'var(--cond-bg)', color: 'var(--cond)' }}><I.review size={17} /></span>
          <div><div className="fc-v">2</div><div className="fc-k">need review</div></div>
        </div>
        <AmbientConsole />
      </div>

      {modal === 'forgot' && <ForgotPassword onClose={() => setModal(null)} onBackToSignin={() => setModal(null)} />}
      {modal === 'request' && <RequestAccess onClose={() => setModal(null)} />}
    </div>
  );
}

Object.assign(window, { Login });
